import { Flex, Tooltip, Typography } from "antd"
import { useTranslation } from "react-i18next"

import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"

const { Text } = Typography

// committed_receivable/committed_payable are summed server-side from ledger
// lines and arrive as plain numbers, so two figures that are equal to the
// cent (e.g. from 33.33 + 33.33 + 33.34) can still differ under `!==` by a
// float artifact like 99.99999999999999. Round to cents before comparing —
// same convention as invoice-totals.ts's roundHalfUp.
const centsEqual = (a: number, b: number): boolean =>
  Math.round((a + Number.EPSILON) * 100) ===
  Math.round((b + Number.EPSILON) * 100)

interface MilestoneFinancialCellProps {
  settled: number | null
  planned: number | null
  /** What the ledger actually holds — read only to decide whether Planned
   * disagrees with it, and named in Planned's tooltip. Never rendered as
   * its own figure in the cell. */
  committed?: number | null
  /** The project's current Contract Value (Receivables) or Contract
   * Expenses (Payables) — the same figure the header and Financial Alerts
   * panel compare against, so a row never disagrees with them. */
  contractLimit?: number | null
  contractLabel?: string
}

/**
 * Two figures, independently hoverable, each flagged on its own rule:
 * Settled turns red when it exceeds Planned (this milestone's own
 * over-collection); Planned turns red when it disagrees with Committed
 * (the plan hasn't been reconciled with the ledger) — two different
 * questions, so exceeding one never flags the other.
 *
 * Both are *also* flagged red when they individually exceed the project's
 * overall contract limit, independent of the two rules above.
 */
export const MilestoneFinancialCell = ({
  settled,
  planned,
  committed,
  contractLimit,
  contractLabel = "Contract"
}: MilestoneFinancialCellProps) => {
  const { t } = useTranslation()
  const safeSettled = Number(settled) || 0
  const safePlanned = Number(planned) || 0
  const safeCommitted = Number(committed) || 0
  const limit = Number(contractLimit) || 0

  const allEmpty = !safeSettled && !safePlanned && !safeCommitted
  const settledOverPlan = !allEmpty && safeSettled > safePlanned
  const committedOffPlan = !allEmpty && !centsEqual(safeCommitted, safePlanned)
  const settledOverContract = limit > 0 && safeSettled > limit
  const plannedOverContract = limit > 0 && safePlanned > limit

  const settledFlagged = settledOverPlan || settledOverContract
  const plannedFlagged = committedOffPlan || plannedOverContract

  const classFor = (flagged: boolean) =>
    flagged ? "text-red-500 font-bold" : "text-teal-600 font-medium"

  const contractLine = (value: number, exceeds: boolean) =>
    exceeds && (
      <>
        <Text className="text-white text-xs">
          {contractLabel}: {budgetFormat(limit)}
        </Text>
        <Text className="text-red-400 text-xs font-semibold">
          Exceeds contract by {budgetFormat(value - limit)}
        </Text>
      </>
    )

  const settledTooltip = (
    <Flex vertical gap={2} className="text-white text-xs">
      <Text className="text-white text-xs">
        {t("milestoneMoney.settled", "Settled")}: {budgetFormat(safeSettled)}
      </Text>
      {settledOverPlan && (
        <Text className="text-red-400 text-xs font-semibold">
          {t(
            "milestoneMoney.settledOverPlan",
            "Settled is above the planned value."
          )}
        </Text>
      )}
      {contractLine(safeSettled, settledOverContract)}
    </Flex>
  )

  const plannedTooltip = (
    <Flex vertical gap={2} className="text-white text-xs">
      <Text className="text-white text-xs">
        {t("milestoneMoney.planned", "Planned")}: {budgetFormat(safePlanned)}
      </Text>
      {committedOffPlan && (
        <>
          <Text className="text-white text-xs">
            {t("milestoneMoney.committed", "Committed")}:{" "}
            {budgetFormat(safeCommitted)}
          </Text>
          <Text className="text-red-400 text-xs font-semibold">
            {t(
              "milestoneMoney.committedOffPlan",
              "Committed does not match the planned value — align the transaction lines with the plan."
            )}
          </Text>
        </>
      )}
      {contractLine(safePlanned, plannedOverContract)}
    </Flex>
  )

  return (
    <Flex justify="center" gap={4} className="text-center whitespace-nowrap">
      <Tooltip title={settledTooltip}>
        <Text
          data-testid="milestone-settled"
          className={classFor(settledFlagged)}
        >
          {budgetFormat(safeSettled)}
        </Text>
      </Tooltip>
      <Text className="text-teal-600 font-medium">/</Text>
      <Tooltip title={plannedTooltip}>
        <Text
          data-testid="milestone-planned"
          className={classFor(plannedFlagged)}
        >
          {budgetFormat(safePlanned)}
        </Text>
      </Tooltip>
    </Flex>
  )
}
